import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { FormikConfig, FormikHelpers } from "formik/dist/types";
import { isNil } from "ramda";

import { ValueRecord } from "../../types";

export interface useAffinityFormOptions<DATA extends ValueRecord = ValueRecord, TMutationResponce = unknown> {
  initialData?: Maybe<DATA>;
  validate?: (value: DATA) => undefined | ValidationErrors | Promise<ValidationErrors>;
  validationSchema?: FormikConfig<unknown>["validationSchema"];
  asyncValidation?: (values: DATA) => undefined | ValidationErrors | Promise<ValidationErrors>;
  confirmHandler?: (values: DATA) => Promise<void | TMutationResponce | undefined | null>;
}
export type useAffinityFormReturn<DATA extends ValueRecord = ValueRecord> = {
  initialData: Maybe<DATA>;
  formData: Maybe<DATA>;
  validate?: (value: DATA) => undefined | ValidationErrors | Promise<ValidationErrors>;
  confirmData: (data: DATA, formikHelpers: FormikHelpers<DATA>) => void;
  submitData: (data?: DATA) => void;
  validationSchema?: FormikConfig<unknown>["validationSchema"];
  isBusy: boolean;
};

type useAffinityFormType = <DATA extends ValueRecord = ValueRecord>(
  options: useAffinityFormOptions<DATA>
) => useAffinityFormReturn<DATA>;

export const useAffinityForm: useAffinityFormType = <DATA extends ValueRecord = ValueRecord>({
  initialData,
  validate,
  validationSchema,
  asyncValidation,
  confirmHandler,
}: useAffinityFormOptions<DATA>) => {
  const [formData, setFormData] = useState<Maybe<DATA>>(null);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const navigate = useNavigate();

  const confirmData = useCallback(
    async (data, formikHelpers) => {
      const applyData = () => {
        setFormData(data);
        navigate("confirm");
      };
      if (asyncValidation) {
        setIsBusy(true);
        const errors = await asyncValidation(data);
        setIsBusy(false);
        if (!errors) {
          applyData();
        } else {
          formikHelpers.setErrors(errors);
        }
      } else {
        applyData();
      }
    },
    [asyncValidation, navigate]
  );

  const submitData = useCallback(() => {
    if (confirmHandler) {
      if (isNil(formData)) {
        return;
      }
      setIsBusy(true);
      confirmHandler(formData).finally(() => {
        setIsBusy(false);
      });
    }
  }, [confirmHandler, formData]);
  return {
    initialData: initialData ?? null,
    formData,
    validate,
    validationSchema,
    confirmData,
    submitData,
    isBusy,
  };
};
